import React, { useState } from "react";
import { formatUSD } from "@/lib/format";
import { useExpenses } from "@/hooks/useExpenses";
import ExpenseForm from "@/components/expenses/ExpenseForm";
import ExpenseFilterBar from "@/components/expenses/ExpenseFilterBar";
import ExpenseTable from "@/components/expenses/ExpenseTable";

const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  expense_date: todayStr(),
  category: "Mileage",
  amount: "",
  miles: "",
  vendor: "",
  notes: "",
  deal_id: "",
};

function buildDraft(item) {
  return {
    expense_date: (item.expense_date || "").slice(0, 10),
    category: item.category,
    amount: item.amount,
    miles: item.miles || "",
    vendor: item.vendor || "",
    notes: item.notes || "",
    deal_id: item.deal_id || "",
  };
}

export default function ExpensesPage() {
  const { items, categories, deals, create, update, remove } = useExpenses();
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("all");
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (await create(form)) {
      setForm({ ...emptyForm, category: form.category });
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditDraft(buildDraft(item));
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditDraft(null);
  };

  const saveEdit = async () => {
    if (await update(editId, editDraft)) cancelEdit();
  };

  const total = items.reduce((s, e) => s + Number(e.amount || 0), 0);
  const visible = filter === "all" ? items : items.filter((e) => e.category === filter);

  return (
    <div className="px-6 lg:px-10 py-8" data-testid="expenses-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Ledger</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Expenses</h1>
          <p className="text-sm text-neutral-500 mt-2">
            Track deductibles. Categorized expenses appear in your tax report.
          </p>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Total logged</div>
          <div className="font-mono text-3xl" style={{ color: "var(--expense)" }}>{formatUSD(total)}</div>
        </div>
      </div>

      <ExpenseForm form={form} setForm={setForm} categories={categories} deals={deals} onSubmit={submit} />
      <ExpenseFilterBar categories={categories} filter={filter} setFilter={setFilter} />
      <ExpenseTable
        items={visible}
        categories={categories}
        deals={deals}
        editId={editId}
        editDraft={editDraft}
        setEditDraft={setEditDraft}
        onEdit={startEdit}
        onCancel={cancelEdit}
        onSave={saveEdit}
        onDelete={remove}
      />
    </div>
  );
}
